// Environment detail — everything deployed in one environment, newest first,
// with account/FQDN metadata for dynamic environments.

import React, { useEffect, useMemo } from 'react';
import { Box, Link as MuiLink, Paper, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { Public as EnvironmentsIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { useV2Filters } from '../hooks/useV2Filters';
import { isInProgressStatus } from '../lib/grouping';
import { deploymentKey, serviceFilterKey, serviceFilterLabel } from '../lib/records';
import { getColors, rounded } from '../theme/tokens';
import { isDestroyOperation } from '../../utils/serviceParser';
import PageHeader from '../components/PageHeader';
import EnvBadge from '../components/EnvBadge';
import MultiSelectFilter from '../components/MultiSelectFilter';
import DeploymentRow from '../components/DeploymentRow';
import EmptyState from '../components/EmptyState';

// Chip tints — match the Services directory.
const APP_HUE = '#0075de';
const JOB_HUE = '#8155b8';

const EnvironmentPage: React.FC = () => {
  const { env = '' } = useParams<{ env: string }>();
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { records, staleCurrentIds, environmentInfo, fetchEnvironmentInfo, wsStatus } =
    useDeploymentData();
  const { apps: selectedApps, services: selectedJobs, setApps, setServices } = useV2Filters();

  useEffect(() => {
    if (wsStatus === 'connected') fetchEnvironmentInfo();
  }, [wsStatus, fetchEnvironmentInfo]);

  const envRecords = useMemo(
    () =>
      records
        .filter(
          r => (r.envName || r.env) === env && (!r.id || !staleCurrentIds.has(r.id))
        )
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
    [records, env, staleCurrentIds]
  );

  // Unique apps in this env and the jobs (serviceName) each deploys.
  const { uniqueApps, appToJobs } = useMemo(() => {
    const appsSet = new Set<string>();
    const map = new Map<string, Set<string>>();
    envRecords.forEach(r => {
      if (!r.app) return;
      appsSet.add(r.app);
      if (r.serviceName) {
        const jobs = map.get(r.app) ?? new Set<string>();
        jobs.add(serviceFilterKey(r.type, r.serviceName));
        map.set(r.app, jobs);
      }
    });
    return { uniqueApps: Array.from(appsSet).sort(), appToJobs: map };
  }, [envRecords]);

  // Job options scoped to the selected app(s), else every job in this env.
  const jobOptions = useMemo(() => {
    const out = new Set<string>();
    const sources = selectedApps.length > 0 ? selectedApps : Array.from(appToJobs.keys());
    sources.forEach(app => appToJobs.get(app)?.forEach(j => out.add(j)));
    return Array.from(out).sort();
  }, [selectedApps, appToJobs]);

  // Changing the app selection re-scopes the job options — drop any selected
  // jobs that no longer belong to a selected app.
  const handleAppsChange = (next: string[]) => {
    setApps(next);
    if (selectedJobs.length === 0) return;
    const allowed = new Set<string>();
    const sources = next.length > 0 ? next : Array.from(appToJobs.keys());
    sources.forEach(app => appToJobs.get(app)?.forEach(j => allowed.add(j)));
    const pruned = selectedJobs.filter(j => allowed.has(j));
    if (pruned.length !== selectedJobs.length) setServices(pruned);
  };

  const filteredRecords = useMemo(() => {
    const appSet = new Set(selectedApps);
    const jobSet = new Set(selectedJobs);
    return envRecords.filter(
      r =>
        (appSet.size === 0 || (r.app != null && appSet.has(r.app))) &&
        (jobSet.size === 0 ||
          (r.serviceName != null && jobSet.has(serviceFilterKey(r.type, r.serviceName))))
    );
  }, [envRecords, selectedApps, selectedJobs]);

  const isDynamic = envRecords.some(r => r.ttl != null);
  const info = environmentInfo.find(i => i.environment_name === env);

  const summary = useMemo(() => {
    const active = envRecords.filter(
      r => r.status?.toLowerCase() === 'success' && !isDestroyOperation(r)
    ).length;
    const deploying = envRecords.filter(r => isInProgressStatus(r.status)).length;
    const failed = envRecords.filter(r => r.status?.toLowerCase() === 'failed').length;
    const destroyed = envRecords.filter(
      r => r.status?.toLowerCase() === 'success' && isDestroyOperation(r)
    ).length;
    return [
      ...(deploying > 0 ? [{ label: `${deploying} deploying`, color: '#0b76d1' }] : []),
      ...(active > 0 ? [{ label: `${active} active`, color: '#1aae39' }] : []),
      ...(failed > 0 ? [{ label: `${failed} failed`, color: '#d93a3a' }] : []),
      ...(destroyed > 0 ? [{ label: `${destroyed} destroyed`, color: '#787774' }] : []),
    ];
  }, [envRecords]);

  return (
    <Box>
      <PageHeader
        crumbs={[{ label: 'Environments', to: '/environments' }, { label: env }]}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {env}
            <EnvBadge env={env} dynamic={isDynamic} />
          </Box>
        }
        subtitle={
          <Box component="span" sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {summary.map(s => (
              <Box component="span" key={s.label} sx={{ color: s.color, fontWeight: 600 }}>
                {s.label}
              </Box>
            ))}
          </Box>
        }
      />

      {info && (
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 3, borderRadius: `${rounded.lg}px`, borderColor: c.hairline }}
        >
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {info.account_name && (
              <Box>
                <Typography variant="overline" sx={{ color: c.inkMuted, display: 'block' }}>
                  Account
                </Typography>
                <Typography variant="body2">{info.account_name}</Typography>
              </Box>
            )}
            {info.region && (
              <Box>
                <Typography variant="overline" sx={{ color: c.inkMuted, display: 'block' }}>
                  Region
                </Typography>
                <Typography variant="body2">{info.region}</Typography>
              </Box>
            )}
            {info.fqdn && (
              <Box>
                <Typography variant="overline" sx={{ color: c.inkMuted, display: 'block' }}>
                  FQDN
                </Typography>
                <MuiLink
                  href={`https://${info.fqdn}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {info.fqdn}
                </MuiLink>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {envRecords.length === 0 ? (
        <EmptyState
          icon={<EnvironmentsIcon />}
          title="Nothing deployed here"
          body="No current records reference this environment — it may have been fully destroyed."
        />
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            {/* App filter (multi-select, URL-synced via ?apps=) */}
            <MultiSelectFilter
              options={uniqueApps}
              value={selectedApps}
              onChange={handleAppsChange}
              hue={APP_HUE}
              placeholder="Filter by app…"
            />
            {/* Job filter (multi-select, scoped to selected apps, URL-synced via ?services=) */}
            <MultiSelectFilter
              options={jobOptions}
              value={selectedJobs}
              onChange={setServices}
              getOptionLabel={serviceFilterLabel}
              hue={JOB_HUE}
              placeholder="Filter by job…"
            />
          </Box>

          {filteredRecords.length === 0 ? (
            <EmptyState
              icon={<EnvironmentsIcon />}
              title="No deployments match these filters"
              body="Adjust or clear the app and job filters to see more."
            />
          ) : (
            <Paper
              variant="outlined"
              sx={{ borderRadius: `${rounded.lg}px`, borderColor: c.hairline, overflow: 'hidden' }}
            >
              {filteredRecords.map((r, i) => (
                <DeploymentRow key={deploymentKey(r, i)} record={r} hideEnv index={i} />
              ))}
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default EnvironmentPage;
